import { IonContent } from '@ionic/react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { useOnboarding } from '@/pages/Onboarding/context';
import { CHAT_INITIATIVE_OPTIONS } from '@/pages/Onboarding/options/ChatInitiative/CHAT_INITIATIVE_OPTIONS';
import { ChatInitiativeOptionsType } from '@/pages/Onboarding/options/ChatInitiative/types';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle } from '../../components/Title';
import { useLayoutFormSelections } from '../../hooks/useLayoutFormSelections';
import { LayoutDataType } from '../../types';

import {
  ChatInitiativeAnswerItemStyles,
  ChatInitiativeAnswersStyles,
  ChatInitiativeStyles,
} from './ChatInitiative.styles';

export const ScreenChatInitiative: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'ChatInitiative',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const chatInitiative = form.watch('chat_initiative');

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['chat_initiative']>,
  ) => {
    form.setValue('chat_initiative', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        chat_initiative: option.title,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(wizard.nextStep);
      }, 200);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  return (
    <ChatInitiativeStyles
      background="dark-violet"
      bgImage={
        <img
          src={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${hairColor}/chat_initiative.jpg`}
          alt="Chat initiative"
        />
      }
    >
      <IonContent>
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <OwnAiTitle
            animate={shouldAnimate}
            show={showElements}
            className="chat-initiative-title"
          >
            Do you want her to text first?
          </OwnAiTitle>

          <ChatInitiativeAnswersStyles>
            {CHAT_INITIATIVE_OPTIONS.map((option) => (
              <Option
                isSelected={chatInitiative === option.id}
                onClick={handleSelect}
                option={option}
                show={showElements}
                animate={shouldAnimate}
                key={option.id}
              />
            ))}
          </ChatInitiativeAnswersStyles>
        </div>
      </IonContent>
    </ChatInitiativeStyles>
  );
};

function Option({
  show,
  animate,
  isSelected,
  option,
  onClick,
}: {
  animate?: boolean;
  show: boolean;
  option: ChatInitiativeOptionsType[number];
  isSelected: boolean;
  onClick: (option: ChatInitiativeOptionsType[number]) => void;
}) {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    const scale = isSelected ? 1.03 : 0.97;

    return (
      <AnimatePresence>
        {show && (
          <ChatInitiativeAnswerItemStyles
            key={option.id}
            answer={option.id}
            isSelected={isSelected}
            onClick={() => onClick(option)}
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1, scale }}
            exit={{ x: 100, opacity: 0 }}
          >
            <UiTypography
              variant="title-medium"
              className="answer-title"
              align="center"
            >
              {option.title}
            </UiTypography>
            <UiTypography
              variant="body-medium"
              className="answer-description"
              align="center"
            >
              {option.description}
            </UiTypography>
          </ChatInitiativeAnswerItemStyles>
        )}
      </AnimatePresence>
    );
  }

  const scale = isSelected ? 1.03 : 1;

  return (
    <ChatInitiativeAnswerItemStyles
      key={option.id}
      answer={option.id}
      isSelected={isSelected}
      onClick={() => onClick(option)}
      initial={{
        scale,
      }}
      animate={{
        scale,
      }}
    >
      <UiTypography variant="title-medium" className="answer-title">
        {option.title}
      </UiTypography>
      <UiTypography variant="body-medium" className="answer-description">
        {option.description}
      </UiTypography>
    </ChatInitiativeAnswerItemStyles>
  );
}
